import { t } from "i18next";
import orderBy from "lodash/orderBy";

import { MaterialGradeSchema, MaterialGradeType } from "shared";

const SEPARATOR = "@@@@";

export const getMaterialName = (material: {
  name: string | null;
  materialGrade: MaterialGradeType;
}) => {
  return material.name ? material.name : t(`qrl.materialGrades.${material.materialGrade}`);
};

export const parseSelectOptionValue = (value: string) => {
  const [materialGrade, ...materialNameParts] = value.split(SEPARATOR);
  const materialName = materialNameParts.join(SEPARATOR);
  const parsedMaterialGrade = MaterialGradeSchema.safeParse(materialGrade);
  return {
    materialGrade: parsedMaterialGrade.success ? parsedMaterialGrade.data : null,
    materialName,
  };
};

export const buildSelectOptionValue = (
  materialGrade: MaterialGradeType | null,
  materialName?: string,
) => {
  if (!materialGrade) {
    return materialName ?? "-";
  }
  return `${materialGrade}${SEPARATOR}${materialName ?? ""}`;
};

export const getMaterialSelectOptions = (
  materials: {
    name: string | null;
    materialGrade: MaterialGradeType;
  }[],
  currentMaterialName: string,
  currentMaterialGrade: MaterialGradeType | null,
) => {
  const options: { label: string; value: string }[] = materials.map((material) => {
    const materialName = getMaterialName(material);

    return {
      label: materialName,
      value: buildSelectOptionValue(material.materialGrade, materialName),
    };
  });

  if (options.length === 0) {
    Object.values(MaterialGradeSchema.Values).forEach((grade: MaterialGradeType) =>
      options.push({
        label: t(`qrl.materialGrades.${grade}`),
        value: buildSelectOptionValue(grade),
      }),
    );
  }

  const uniqueOptions = Array.from(new Map(options.map((item) => [item.value, item])).values());

  const sortedOptions = orderBy(uniqueOptions, ["label"], ["asc"]);

  const currentIsInOptions = sortedOptions.find(
    (o) => o.value === buildSelectOptionValue(currentMaterialGrade, currentMaterialName),
  );
  if (!currentIsInOptions) {
    sortedOptions.push({
      label: currentMaterialName
        ? currentMaterialName
        : currentMaterialGrade
          ? t(`qrl.materialGrades.${currentMaterialGrade}`)
          : "-",
      value: buildSelectOptionValue(currentMaterialGrade, currentMaterialName),
    });
  }

  return sortedOptions;
};

export const findMaterialFromSelectOptions = (
  selectValue: string,
  materials: {
    name: string | null;
    materialGrade: MaterialGradeType;
  }[],
): {
  materialName: string;
  materialGrade: MaterialGradeType | null;
} => {
  const { materialGrade: selectedMaterialGrade, materialName: selectedMaterialName } =
    parseSelectOptionValue(selectValue);

  const selectedMaterial = materials.find(
    (material) => getMaterialName(material) === selectedMaterialName,
  );

  if (selectedMaterial) {
    return {
      materialName: getMaterialName(selectedMaterial),
      materialGrade: selectedMaterial.materialGrade,
    };
  }

  const materialGradeValues = Object.values(MaterialGradeSchema.Values);
  if (selectedMaterialGrade && materialGradeValues.includes(selectedMaterialGrade)) {
    return {
      materialName: "",
      materialGrade: selectedMaterialGrade,
    };
  }

  return {
    materialName: selectedMaterialName,
    materialGrade: selectedMaterialGrade ?? null,
  };
};
